/**
 * AgedCareSuggestionEngineDeepDive.js
 * Deep Dive modal for the Aged Care Suggestion Engine: opportunity cost, yearly projections, asset breakdown.
 * Extracted from AgedCareSuggestionEnginePanel.js for maintainability.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { EyeOff } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { ProjectionsView } from './ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from './projectionsModalPreferences.ts';
import { formatRadStrategyDisplayName } from './futureLifeEventUtils.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { applyAgedCareDownsizeToFactFind } from '../../../../shared/src/calculation/agedCareDownsizeFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions as SharedStrategyOptions } from '../../../../shared/src/calculation/strategyOptionsTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

// Same asset-map shape as factFind.assets (see types/formDraft.ts) — each projection year row
// snapshots the fact-find's assets/liabilities as they stood that year.
type OpeningAssetsMap = NonNullable<DraftFactFind['assets']>;

// The suggestion engine's per-year projection row (calc-engine REST response — see calculationApi.ts's
// header on why these stay loosely typed rather than modelled 1:1) — only the fields this modal reads.
export interface DeepDiveProjectionBreakdownItem {
    label: string;
    value: string | number;
    change?: string;
}

interface DeepDiveFyProjectionRow {
    socialSecurity?: number;
    annualCareCost?: number;
    [key: string]: unknown;
}

interface DeepDiveFyFullYearView {
    daysPre?: number;
    daysPost?: number;
    fyLabel?: string;
    netWorthOpening?: number;
    blended?: {
        socialSecurity?: number;
        annualCareCost?: number;
        cashFlow?: number;
        tax?: number;
        livingExpenses?: number;
        [key: string]: unknown;
    };
    baselineProjectionRow?: DeepDiveFyProjectionRow;
    strategyProjectionRow?: DeepDiveFyProjectionRow;
    [key: string]: unknown;
}

export interface DeepDiveProjectionYear {
    netWorth?: number;
    cashFlow?: number;
    cashflow?: number;
    socialSecurity?: number;
    socialSecurityBreakdown?: DeepDiveProjectionBreakdownItem[];
    annualCareCost?: number;
    agedCareBreakdown?: DeepDiveProjectionBreakdownItem[];
    cashFlowBreakdown?: DeepDiveProjectionBreakdownItem[];
    /** Human-readable HTML breakdown of the year's calculations (see server `ProjectionYearResult.breakdown`). */
    breakdown?: string;
    cashflowRoutingLine?: DeepDiveProjectionBreakdownItem;
    tax?: number;
    fyLabel?: string;
    isPartialFy?: boolean;
    periodStart?: string;
    periodEnd?: string;
    home?: { value?: number; description?: string };
    assets?: OpeningAssetsMap;
    fyFullYearView?: DeepDiveFyFullYearView;
    [key: string]: unknown;
}

export interface DeepDiveScenarioSnapshot {
    pension?: number;
    totalAgedCareCosts?: number;
    investmentIncome?: number | string;
    livingExpenses?: number;
    agePensionBreakdown?: DeepDiveProjectionBreakdownItem[];
    [key: string]: unknown;
}

export interface DeepDiveScenarioStrategyOptions {
    downsizeHome?: boolean;
    downsizeDetails?: {
        newHomePurchasePrice?: number;
        radAllocation?: number;
        [key: string]: unknown;
    };
    sellingCosts?: number | string;
    rentAmount?: number;
    rentExpenses?: number;
    [key: string]: unknown;
}

export interface DeepDiveAssetAllocationItem {
    assetName: string;
    assetId?: string | number;
    status?: 'sold' | 'partiallySold' | 'retained' | string;
    amount?: number;
    liquidatedAmount?: number;
    [key: string]: unknown;
}

export interface DeepDiveScenario {
    name?: string;
    snapshot?: DeepDiveScenarioSnapshot;
    projections?: DeepDiveProjectionYear[];
    strategyOptions?: DeepDiveScenarioStrategyOptions;
    radAmount?: number;
    radFunding?: Record<string, number | string>;
    assetAllocation?: DeepDiveAssetAllocationItem[];
    originalFactFind?: { assets?: Record<string, Record<string, unknown>[]> };
    [key: string]: unknown;
}

interface DeepDiveAnalysis {
    strategies?: DeepDiveScenario[];
    [key: string]: unknown;
}

interface AgedCareSuggestionEngineDeepDiveProps {
    selectedScenario: DeepDiveScenario;
    factFind: DraftFactFind;
    agedCareCosts?: ({ scenarioLivingExpenses?: number } & Record<string, unknown>) | null;
    legacyYears: number;
    analysis?: DeepDiveAnalysis | null;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    onSelectStrategy: () => void;
    onClose: () => void;
    applyRadFundingToFactFind: (factFind: DraftFactFind, scenario: unknown) => DraftFactFind;
    /*
     * The case-level parameters the ranking run was given. Carried in so the calendar-year re-run
     * below is the SAME evaluation with one thing changed — a re-run that quietly dropped the entry
     * date or the deposit arrangement would differ for reasons that have nothing to do with the
     * calendar, which is the whole failure this feature has to avoid.
     */
    careStartDate?: string;
    radDrawdown?: 'fees' | 'dap' | 'none';
    residentialFeeArrangements?: '2014' | '2025';
}

export function AgedCareSuggestionEngineDeepDive({
    selectedScenario,
    factFind,
    agedCareCosts,
    legacyYears,
    analysis,
    activeTab,
    setActiveTab,
    onSelectStrategy,
    onClose,
    applyRadFundingToFactFind,
    careStartDate,
    radDrawdown,
    residentialFeeArrangements,
}: AgedCareSuggestionEngineDeepDiveProps) {
    const [year1OpeningBalance, setYear1OpeningBalance] = useState(0);
    // Post-downsize, post-RAD-funding factFind for Year 1 — computed once here (downsizing now runs
    // server-side) and reused below wherever the render body needs "opening state for Year 1", instead
    // of recomputing it synchronously inline (which can't await a network call).
    const [year1ModifiedFactFind, setYear1ModifiedFactFind] = useState<DraftFactFind | null>(null);

    useEffect(() => {
        let cancelled = false;
        // DraftFactFind is structurally compatible with FactFind/StrategyOptions
        // (see types/formDraft.ts); the shared calc-engine fn defensively coerces internally
        // regardless, and its own return type is `any` (agedCareDownsizeFactFind.ts).
        const baseFactFind: DraftFactFind = selectedScenario.strategyOptions?.downsizeHome
            ? applyAgedCareDownsizeToFactFind(factFind as unknown as FactFind, selectedScenario.strategyOptions as SharedStrategyOptions)
            : factFind;
        const modifiedFactFind = applyRadFundingToFactFind(baseFactFind, selectedScenario);
        setYear1ModifiedFactFind(modifiedFactFind);
        calculationApi.calculateFinancials(modifiedFactFind, {}, 0).then((financials) => {
            if (!cancelled) setYear1OpeningBalance(financials.netWorth || 0);
        });
        return () => { cancelled = true; };
    }, [selectedScenario, factFind, applyRadFundingToFactFind]);

    /*
     * THE YEAR TOGGLE, BY RE-RUNNING THIS ONE CANDIDATE (owner, 23 Sep 2026).
     *
     * The deep dive used to hide the financial/calendar toggle, and the reason given — that it had no
     * re-run pathway — was wrong: it is the same engine. What is true, and what decides the design, is
     * that the fact-find a candidate was PROJECTED from is derived on the server (the scenario
     * applied, the downsize applied, the means-test lever applied) and never sent here. Rebuilding it
     * on this side would produce a plausible set of rows for a subtly DIFFERENT plan — the worst kind
     * of wrong, because nothing on screen would say so.
     *
     * So the re-run goes back through the engine's own entry point with this one scenario in the
     * array. One derivation, no second implementation to drift. `agedCare.test.ts` pins that a
     * single-scenario run reproduces the row the whole field produced, which is what lets the reader
     * trust that this is the candidate that was ranked.
     */
    const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
    const [calendarYearProjections, setCalendarYearProjections] = useState<DeepDiveProjectionYear[] | null>(null);
    useEffect(() => {
        if (modalPeriodMode !== 'calendar_year' || activeTab !== 'yearlyProjections') { setCalendarYearProjections(null); return undefined; }
        let cancelled = false;
        setCalendarYearProjections(null);
        // The scenario as the engine takes it — the same fields it was generated with, read back off
        // the row it produced.
        const scenario = {
            name: selectedScenario.name,
            radFunding: selectedScenario.radFunding ?? {},
            strategyOptions: selectedScenario.strategyOptions ?? {},
            ...(selectedScenario.groupKey ? { groupKey: selectedScenario.groupKey } : {}),
            ...(selectedScenario.fundingOrderLabel ? { fundingOrderLabel: selectedScenario.fundingOrderLabel } : {}),
            ...(selectedScenario.meansTestStrategy ? { meansTestStrategy: selectedScenario.meansTestStrategy } : {}),
        };
        calculationApi.evaluateAgedCareRadFundingScenarios(
            factFind,
            (agedCareCosts ?? {}) as never,
            [scenario] as never,
            legacyYears,
            careStartDate,
            radDrawdown,
            residentialFeeArrangements,
            'calendar_year',
        ).then((rows: unknown) => {
            if (cancelled) return;
            const first = Array.isArray(rows) ? (rows[0] as { projections?: DeepDiveProjectionYear[] } | undefined) : undefined;
            setCalendarYearProjections(first?.projections ?? []);
        });
        return () => { cancelled = true; };
    }, [modalPeriodMode, activeTab, selectedScenario, factFind, agedCareCosts, legacyYears, careStartDate, radDrawdown, residentialFeeArrangements]);

    /**
     * Rendered into `document.body`, and this is the whole reason the modal was still boxed in after
     * being widened.
     *
     * `position: fixed` is relative to the VIEWPORT only while no ancestor establishes a containing
     * block — and `transform`, `filter` and `backdrop-filter` all do. This modal renders inside the
     * shared `Card`, which carries `backdrop-blur-xl`, so `fixed inset-0` was resolving against the
     * card's box rather than the screen: the overlay could never be wider than the column it sat in,
     * whatever max-width it was given. Manual Control's projections modal is not inside a Card, which
     * is why the identical markup behaves differently there.
     *
     * A portal removes the dependency entirely rather than relying on no future ancestor introducing
     * one. The Save Warning modal in the sibling panel already does this.
     */
    return createPortal(
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    {/* Same shell as Manual Control's projections modal, deliberately.
                        This carried the same year-by-year charts and tables in a 896px box
                        (`max-w-4xl`), which left an adviser reading a five-year cashflow chart and a
                        five-column table through a slot half the width of the one the identical content
                        gets in Manual Control. The header and footer are now fixed and the BODY scrolls
                        (`flex flex-col overflow-hidden` + an `overflow-y-auto` child), rather than the
                        whole card scrolling — so the strategy name and Select button stay in view while
                        someone reads down the projections. */}
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-screen-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200/80">
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-slate-50/90 via-white to-teal-50/40">
                            <div className="flex justify-between items-center gap-4">
                                <h3 className="text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-sky-500 to-teal-500 flex-1 min-w-0 truncate">{formatRadStrategyDisplayName(selectedScenario.name)}</h3>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onSelectStrategy();
                                        }}
                                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                                    >
                                        Select strategy
                                    </button>
                                    <button
                                        onClick={() => onClose()}
                                        className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                                        title="Close"
                                    >
                                        <EyeOff className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>
                            <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                        </div>
                        {/* The scroller. `min-h-0` is what actually lets it shrink inside the flex
                            column — without it a flex child refuses to go below its content height and
                            the card grows past the viewport instead of scrolling. */}
                        <div className="p-6 flex-1 min-h-0 overflow-y-auto">

                            {/* Tab Navigation */}
                            <div className="border-b border-gray-200 mb-6">
                                <nav className="-mb-px flex space-x-8">
                                    {['yearlyProjections', 'opportunityCost'].map((tab) => (
                            <button
                                            key={tab}
                                            onClick={() => setActiveTab(tab)}
                                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                                activeTab === tab
                                                    ? 'border-blue-500 text-blue-600'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                        >
                                            {tab === 'opportunityCost' && 'Overall Opportunity Cost'}
                                            {tab === 'yearlyProjections' && 'Yearly Projections'}
                                            {tab === 'assetBreakdown' && 'Asset Breakdown'}
                            </button>
                        ))}
                    </nav>
                </div>

                                 {/* Tab Content */}
                            <div className="space-y-6 transition-all duration-300 ease-in-out">
                        {activeTab === 'opportunityCost' && (() => {
                                    const doNothingScenario = analysis?.strategies?.find(s => s.name === 'Do Nothing');
                                    if (!doNothingScenario) {
                                        return <p className="text-sm text-slate-600">No &quot;Do Nothing&quot; baseline is available to compare against.</p>;
                                    }
                                    const num = (v: unknown): number => {
                                        const n = typeof v === 'number' ? v : parseFloat(String(v));
                                        return Number.isFinite(n) ? n : 0;
                                    };
                                    // THE opportunity-cost number: what the estate is projected to be worth after the
                                    // full horizon under this strategy versus doing nothing. Everything else on this
                                    // tab is a per-year DRIVER of this figure, not the figure itself.
                                    const legacyDelta = num(selectedScenario.snapshot?.legacyValue) - num(doNothingScenario.snapshot?.legacyValue);
                                    // Every driver uses ONE sign convention: positive = better off under this
                                    // strategy. Care costs are therefore presented as the SAVING (do nothing minus
                                    // strategy). The old tab inverted the convention per tile — investment income was
                                    // "do nothing minus strategy" with a red plus-sign, care savings were hard-coded
                                    // green, and a fallback invented DAP savings from RAD x MPIR whenever the two
                                    // snapshots happened to be equal. Each strategy now carries its own care-cost
                                    // schedule (strategySpecificAgedCareCosts), so the snapshots already differ where
                                    // they should and the numbers can speak for themselves.
                                    const drivers = [
                                        {
                                            key: 'pension',
                                            title: 'Age Pension',
                                            period: 'per year',
                                            subtitle: 'Change in Age Pension under this strategy. Paying a RAD or restructuring assets can move the means test in your favour.',
                                            value: num(selectedScenario.snapshot?.pension) - num(doNothingScenario.snapshot?.pension),
                                        },
                                        {
                                            key: 'care',
                                            title: 'Aged care cost saving',
                                            period: 'per year',
                                            subtitle: 'Care costs avoided under this strategy — mostly DAP no longer payable on the part of the room price covered by a RAD.',
                                            value: num(doNothingScenario.snapshot?.totalAgedCareCosts) - num(selectedScenario.snapshot?.totalAgedCareCosts),
                                        },
                                        {
                                            key: 'investment',
                                            title: 'Investment income',
                                            period: 'per year (Year 1)',
                                            subtitle: 'Change in investment income. Usually LOWER under a RAD strategy — money paid into the RAD stops earning — and the pension and care-cost gains above are what that trade-off buys.',
                                            value: num(selectedScenario.snapshot?.investmentIncome) - num(doNothingScenario.snapshot?.investmentIncome),
                                        },
                                    ];
                                    const tone = (v: number) =>
                                        Math.round(v) === 0
                                            ? { bg: 'bg-gradient-to-br from-slate-50 to-slate-100', border: 'border-slate-200', text: 'text-slate-800', value: 'text-slate-700' }
                                            : v > 0
                                              ? { bg: 'bg-gradient-to-br from-green-50 to-green-100', border: 'border-green-200', text: 'text-green-800', value: 'text-green-600' }
                                              : { bg: 'bg-gradient-to-br from-red-50 to-red-100', border: 'border-red-200', text: 'text-red-800', value: 'text-red-600' };
                                    const signed = (v: number) => `${v >= 0 ? '+' : ''}${formatCurrency(v)}`;
                                    const legacyTone = tone(legacyDelta);
                                    return (
                                    <div className="space-y-6 animate-fadeIn">
                                        <div className="text-center mb-2">
                                            <h4 className="text-2xl font-bold text-gray-800 mb-2">Opportunity Cost Analysis</h4>
                                            <p className="text-gray-600">{selectedScenario?.name || 'Selected strategy'} vs doing nothing</p>
                                        </div>

                                        {/* The headline: overall estate impact over the projection horizon. */}
                                        <div className={`${legacyTone.bg} p-8 rounded-2xl border-2 ${legacyTone.border} shadow-lg text-center`}>
                                            <h5 className={`text-lg font-bold ${legacyTone.text}`}>Estate impact after {legacyYears} years</h5>
                                            <div className={`text-4xl font-bold ${legacyTone.value} mt-2`}>{signed(legacyDelta)}</div>
                                            <p className={`text-sm ${legacyTone.text} opacity-80 mt-3 max-w-2xl mx-auto`}>
                                                Projected net worth after {legacyYears} years under this strategy, compared with doing nothing.
                                                This single figure nets out every annual effect below, compounded over the full period —
                                                {legacyDelta >= 0 ? ' this strategy is projected to leave the estate better off.' : ' the annual benefits do not fully offset what this strategy gives up.'}
                                            </p>
                                        </div>

                                        {/* The per-year drivers behind that figure — one sign convention throughout:
                                            positive (green) = better off under this strategy. */}
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                            {drivers.map((driver) => {
                                                const t = tone(driver.value);
                                                return (
                                                    <div key={driver.key} className={`${t.bg} p-6 rounded-2xl border-2 ${t.border} shadow-md`}>
                                                        <div className="flex flex-col items-center text-center h-full">
                                                            <h5 className={`text-lg font-bold ${t.text} leading-tight mb-2`}>{driver.title}</h5>
                                                            <p className={`text-sm ${t.text} opacity-80 mb-4 leading-relaxed`}>{driver.subtitle}</p>
                                                            <div className="mt-auto">
                                                                <div className={`text-3xl font-bold ${t.value} mb-1`}>{signed(driver.value)}</div>
                                                                <div className={`text-sm ${t.text} opacity-70 font-medium`}>{driver.period}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Side-by-side values behind each driver. */}
                                        <div className="bg-white p-6 rounded-xl border border-gray-200">
                                            <h5 className="text-lg font-semibold text-gray-800 mb-4 text-center">Where the differences come from</h5>
                                            <div className="space-y-4">
                                                {[
                                                    { name: 'Age Pension (per year)', doNothing: num(doNothingScenario.snapshot?.pension), selected: num(selectedScenario.snapshot?.pension), betterWhenHigher: true },
                                                    { name: 'Aged care costs (per year)', doNothing: num(doNothingScenario.snapshot?.totalAgedCareCosts), selected: num(selectedScenario.snapshot?.totalAgedCareCosts), betterWhenHigher: false },
                                                    { name: 'Investment income (Year 1)', doNothing: num(doNothingScenario.snapshot?.investmentIncome), selected: num(selectedScenario.snapshot?.investmentIncome), betterWhenHigher: true },
                                                    { name: `Projected net worth after ${legacyYears} years`, doNothing: num(doNothingScenario.snapshot?.legacyValue), selected: num(selectedScenario.snapshot?.legacyValue), betterWhenHigher: true },
                                                ].map((row) => {
                                                    const advantage = row.betterWhenHigher ? row.selected - row.doNothing : row.doNothing - row.selected;
                                                    const t = tone(advantage);
                                                    return (
                                                        <div key={row.name} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center bg-gray-50 rounded-lg p-4">
                                                            <div className="text-sm font-semibold text-gray-800">{row.name}</div>
                                                            <div className="text-center">
                                                                <div className="text-xs text-gray-500">Do nothing</div>
                                                                <div className="text-base font-bold text-gray-800">{formatCurrency(row.doNothing)}</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-xs text-gray-500">{selectedScenario?.name || 'This strategy'}</div>
                                                                <div className="text-base font-bold text-blue-600">{formatCurrency(row.selected)}</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-xs text-gray-500">{row.betterWhenHigher ? 'Difference' : 'Saving'}</div>
                                                                <div className={`text-base font-bold ${t.value}`}>{signed(advantage)}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-4">
                                                Annual figures are Year 1 rates. The {legacyYears}-year estate figure will not equal the annual
                                                differences multiplied out: balances compound, the Age Pension and care fees are reassessed each
                                                year as assets change, and one-off effects (like capital gains tax on assets sold) land once.
                                            </p>
                                        </div>
                                    </div>
                                    );
                                })()}
                                {activeTab === 'yearlyProjections' && (
                                    <div className="animate-fadeIn -mx-6">
                                        {/* The SAME projections view the manual control's modal renders — charts accordion,
                                            "How to read this", the table with its tooltips, per-year month-by-month
                                            calculation breakdowns, and the future/today's-dollars toggle — so the two
                                            surfaces cannot drift apart. Differences are inputs, not code:
                                            - agedCareCosts: this STRATEGY's costs (RAD paid => lower DAP), not the panel's.
                                            - factFind/currentFinancials: the post-strategy year-1 opening position.
                                            - the financial/calendar toggle re-runs THIS candidate through the
                                              engine (see the effect above), because the fact-find it was
                                              projected from never leaves the server. */}
                                        <ProjectionsView
                                            projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : (selectedScenario.projections ?? [])) as never}
                                            onPeriodModeChange={setModalPeriodMode}
                                            isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
                                            agedCareCosts={((selectedScenario as { strategySpecificAgedCareCosts?: Record<string, unknown> }).strategySpecificAgedCareCosts ?? agedCareCosts) as never}
                                            factFind={year1ModifiedFactFind ?? factFind}
                                            currentFinancials={{ netWorth: year1OpeningBalance }}
                                        />
                                    </div>
                                )}
            </div>
            </div>
                    </div>
                </div>,
        document.body,
);
}
